import { Form, Typography, Button } from 'antd';
import _ from 'lodash';
import React, {  useCallback, useState, useMemo } from 'react';
import message from "components/CustomMessage";
import { FieldTypes } from 'utils/formUtils';
import { generateFormFields } from 'utils/formUtils';
import { CATEGORIZATION_DEFAULT_ACTIVE_KEY } from 'views/Inventory/Product/ProductFormPopup';
import { getSecurityAndComplianceFields } from 'views/Inventory/common';
import { PermissionSetMap } from 'constants/RBACConstants';
import AuthorisedContainer from "components/AuthorisedContainer";

const {Title} = Typography

const defaultTierValues = {
    "tier1": {
        "publicCloud":true,
        "availability":"High",
        "internetFacing":true,
        "attackingVector":"Network",
        "confidentiality":"NONE",
        "confidentialityOptions":"",
        "availabilityRequirement":"High",
        "confidentialityRequirement":"High"
    },
    "tier2": {
        "publicCloud":true,
        "availability":"Low",
        "internetFacing":true,
        "attackingVector":"Network",
        "confidentiality":"NONE",
        "confidentialityOptions":"",
        "availabilityRequirement":"Medium",
        "confidentialityRequirement":"Medium"
        },
    "tier3":{
        "publicCloud":false,
        "availability":"None",
        "internetFacing":false,
        "attackingVector":"Local",
        "confidentiality":"NONE",
        "confidentialityOptions":"",
        "availabilityRequirement":"Low",
        "confidentialityRequirement":"Low"
        }
    }

function TierPresetConfiguration({globalSettings, updateGlobalSettings}){
    const defaultActiveKey = CATEGORIZATION_DEFAULT_ACTIVE_KEY;
    const tiersOptions = useMemo(() => ([{ label: 'Tier 1', value: 'tier1'}, { label: 'Tier 2', value: 'tier2'}, { label: 'Tier 3', value: 'tier3'}]), []);
    const [form] = Form.useForm();
    const [tier, setTier] = useState('tier1');
    const setInitialConfidentialOptions = useCallback(
        (value) => {
            return getConfidentialOptions(globalSettings, (value || tier));
        },
    [globalSettings, tier]);
    const [confidentialityOption, setConfidentialityOption] = useState(setInitialConfidentialOptions());

    const getConfidentialData = useCallback(
        (options) => {
            const keys = Object.keys(options || confidentialityOption);
            const length = keys.length;
            return length === 3 ? "ALL" : length === 2 ? "TWO" : length === 1 ? "ONE" : "NONE";
        },
    [confidentialityOption]);

    const [confidentiality, setConfidentiality] = useState(getConfidentialData());

    const updateGlobalValues = useCallback(
        async (values) => {
            await form.validateFields();
            const formFields = form.getFieldsValue();
            let selectedTier = formFields.tier || tier;
            const fields = _.omit(formFields, ['tier', 'confidentiality_custom']);
            const tierCategory = globalSettings.tierCategory;
            const _confidentialityOption = values ? values.confidentialityOption : confidentialityOption;
            const payload = {
                confidentialityOptions: Object.keys(_confidentialityOption).join(','),
                confidentiality: values ? values.confidentiality : confidentiality,
                ...fields
            }
            Object.assign(tierCategory, {[selectedTier]: {...tierCategory[selectedTier], ...payload}});

            const messageReference = message.loading("Updating...");
            await updateGlobalSettings({tierCategory});
            setConfidentialityOption(_.isEmpty(_confidentialityOption) ? {C0: false, C1: false, C2: false} : _confidentialityOption)
            messageReference();
            message.success("Updated Successfully")
    }, [form, updateGlobalSettings, confidentiality, confidentialityOption, tier, globalSettings.tierCategory])

    const onConfidentialityChange = useCallback(
        (e, id) => {
            const _confidentialityOption = {...confidentialityOption};
            id = "C"+id;
            if(e.target.checked){
                _confidentialityOption[id] = true;
            } else {
                delete _confidentialityOption[id];
            }
            setConfidentialityOption(_confidentialityOption);
            const _confidentiality = getConfidentialData(_confidentialityOption)
            setConfidentiality(_confidentiality);
            updateGlobalValues({confidentiality: _confidentiality, confidentialityOption: _confidentialityOption});
    }, [confidentialityOption, updateGlobalValues, getConfidentialData]);

    const securityComplianceFields = getSecurityAndComplianceFields({ onConfidentialityChange, confidentialityData: confidentialityOption, defaultActiveKey, permission: PermissionSetMap.UPDATE_GLOBAL_SETTING })

    const handleTierChange = useCallback(
        (value) => {
            const cOptions = setInitialConfidentialOptions(value);
            const cData = getConfidentialData(cOptions);
            setConfidentialityOption(cOptions);
            setConfidentiality(cData);
            setTier(value);            
            form.setFieldsValue(tier);
            updateGlobalValues({confidentiality: cData, confidentialityOption: cOptions});
        },
    [tier, form, updateGlobalValues, setInitialConfidentialOptions, getConfidentialData]);

    const getTierFields = useCallback(
        () => {
            const tierCategory = globalSettings.tierCategory;
            const defaultFields = _.cloneDeep(getKeyValuePair(tierCategory[tier]));
            const fields = form.getFieldsValue();
            defaultFields.forEach(d => {
                if (!_.isEmpty(fields) && fields.hasOwnProperty(d?.name)) {
                    fields[d.name] = d.value;
                } else if (securityComplianceFields.initialValues.hasOwnProperty(d?.name)) {
                    securityComplianceFields.initialValues[d.name] = d.value;
                }
            });
            if (!_.isEmpty(fields)) {
                form.setFieldsValue(fields);
            }
            return securityComplianceFields.fields;
        },
    [securityComplianceFields, tier, form, globalSettings.tierCategory])

    const fields = useMemo(() => (
        [
            {
                name: 'tier',
                type: FieldTypes.SELECT,
                colSpan: 12,
                options: tiersOptions,
                permission: PermissionSetMap.UPDATE_GLOBAL_SETTING,
                props: {
                    onChange: handleTierChange,
                    defaultValue: tiersOptions[0]?.value,
                }
            },
            ...(getTierFields())
        ]
    ), [tiersOptions, handleTierChange, getTierFields]);

    const onValuesChange = useCallback(async (value) => {
        if (!value.tier) {
            await form.validateFields();
            updateGlobalValues();
        }
    }, [form, updateGlobalValues]);

    const onReset = useCallback(()=> {
        const fields = form.getFieldsValue();
        const defaultFields = _.cloneDeep(getKeyValuePair(defaultTierValues[tier]));
        defaultFields.forEach(d => {
            if (!_.isEmpty(fields) && fields.hasOwnProperty(d?.name)) {
                fields[d.name] = d.value;
            }
        });
        form.setFieldsValue(fields);
        updateGlobalValues({confidentialityOption: ""});
    }, [form, tier, updateGlobalValues])

    return (
        <>
            <Title level={5}>Tier Presets Configuration</Title>
            <div className='d-flex justify-content-between'>
            <div>You can configure Tier presets from here.</div>
            <AuthorisedContainer
                tooltip="Reset to default values"
                permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}
            >
                <Button type="primary" size="small" onClick={onReset}>
                    Reset to Default
                </Button>
            </AuthorisedContainer>
            </div>
            <Form
                className="m-t-sm"
                layout="vertical"
                form={form}
                initialValues={{...securityComplianceFields.initialValues, tierCategory: tier}}
                onValuesChange={onValuesChange}
            >
                {generateFormFields(fields)}
            </Form>
        </>
    )
}

export function getConfidentialOptions(data, tier) {
    let confidentialityOption = {};
    const _tier = data?.tierCategory?.[tier] ?? {};
    if(_.isString(_tier.confidentialityOptions) && _tier.confidentialityOptions.length > 0){
        _tier.confidentialityOptions.split(",").forEach((key) => {
            confidentialityOption[key.trim()] = true;
        });
    }
    return confidentialityOption
}

export function getKeyValuePair(obj) {
    let arr = [];
    if (_.isObject(obj)) {
        Object.keys(obj).forEach((k) => {
            arr.push({name: [k], value: obj[k]})
        })
    }
    return arr;
}

export default TierPresetConfiguration